import { CancelToken } from 'axios';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { PARAM_SUGGESTED_FOR_USER } from 'hb-react/shared/apps/TopicsIndexV2/constants/queryParams';
import fetchTopics from 'hb-react/shared/apps/TopicsIndexV2/requests/fetchTopics';
import removeDuplicateTopics from 'hb-react/shared/apps/TopicsIndexV2/utils/removeDuplicateTopics';
import { CANCEL_FETCH_MESSAGE } from 'hb-react/shared/constants/cancelFetch';

import { ITEMS_PER_ROWS_DEFAULT } from './constants';
import getNbItemsPerPage from './utils/getNbItemsPerPage';

const useSuggestedGroups = () => {
  const breakpoint = useBreakpoint();
  const navigate = useNavigate();

  const [topics, setTopics] = useState([]);
  const [totalTopics, setTotalTopics] = useState(0);
  const [isLoaded, setLoaded] = useState(false);

  const cancelTokenRef = useRef(null);
  const mountedRef = useRef(true);

  const itemsPerRow = ITEMS_PER_ROWS_DEFAULT[breakpoint];
  const perPage = getNbItemsPerPage(itemsPerRow);
  const params = { [PARAM_SUGGESTED_FOR_USER]: true };

  const onSeeAll = () => navigate(Routes.topics_path());

  const handleFetchTopics = useCallback(async () => {
    if (isLoaded && cancelTokenRef.current) {
      cancelTokenRef.current.cancel(CANCEL_FETCH_MESSAGE);
    }
    cancelTokenRef.current = CancelToken.source();

    const response = await fetchTopics({
      cancelToken: cancelTokenRef?.current,
      per_page: perPage,
      v2: true,
      ...params,
    });

    if (mountedRef.current && response?.topics) {
      setTopics((prevState) =>
        removeDuplicateTopics([...prevState, ...response.topics]),
      );
      setTotalTopics(response.total);
      setLoaded(true);
    }
  }, [isLoaded, mountedRef.current]);

  useEffect(() => {
    if (mountedRef.current) {
      handleFetchTopics();
    }

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    isLoaded,
    onSeeAll,
    topics,
    totalTopics,
  };
};

export default useSuggestedGroups;
